import { QueryFunctionContext, useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { EventQuery } from '../types';
import { buildQuery } from '../utils';
import { useAuthentication } from './use-authentication';

const requestEndpoint = async ({
  queryKey,
  pageParam = 1,
}: QueryFunctionContext<
  [string, EventQuery, (query: string) => Promise<any>]
>) => {
  const [_, config, endPoint] = queryKey;
  const query = buildQuery({
    ...config,
    page: pageParam.toString(),
    perPage: '20',
  });
  const response = await endPoint(query);
  return response;
};

export const usePaginatedFetch = (
  config: EventQuery,
  queryName: string,
  endPoint: (query: string) => Promise<any>,
) => {
  // State to track if the initial load is completed
  const [isInitialLoad, setIsInitialLoad] = useState<boolean | null>(null);
  const { token } = useAuthentication();
  const {
    data,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    status,
    ...queryProps
  } = useInfiniteQuery([queryName, config, endPoint], requestEndpoint, {
    enabled: !!token,
    getNextPageParam: lastPage => {
      const { pagination } = lastPage;

      // Stop fetching if there are no more pages
      if (pagination.page >= pagination.totalPages) return undefined;

      // Otherwise, proceed to the next page
      return pagination.page + 1;
    },
    onSuccess: () => {
      // Set isInitialLoad to false after the first successful response
      if (isInitialLoad === null) {
        setIsInitialLoad(false);
      } else if (isInitialLoad) {
        setIsInitialLoad(true);
      }
    },
    retry: (failureCount, error) => {
      // Only retry if the error is NOT 401 (Unauthorized)
      if ((error as any).response?.status === 401) {
        return false;
      }
      return failureCount < 3; // Retry up to 3 times for other errors, here 3 is default value for useQuery failureCount
    },
    staleTime: 5 * 60 * 1000, // Data stays fresh for 5 minutes
    cacheTime: 10 * 60 * 1000, // Unused data is kept in cache for 10 minutes
  });

  return {
    data,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isInitialLoad,
    status,
    ...queryProps,
  };
};
